import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { getBaseUnitType } from '../utils/measurementConversions';
import { convertToBaseUnit } from '../utils/measurementConversions';
import axios from 'axios'; // Make sure axios is imported
import { API_BASE_URL } from '../config';
import { useCookies } from 'react-cookie';
import '../styles/recipecosts.css';
import '../styles/specials.css';
import '../styles/recipe-price-details-modern.css';
import { useNavigate } from 'react-router-dom';
import vitalityBadgeImg from './vitality.png';

/** Server may send boolean true, string "true", or omit; Mongo export quirks */
function isVitalityBrand(brand) {
    if (!brand) return false;
    const v = brand.vitality;
    if (v === true || v === 1) return true;
    if (typeof v === 'string' && v.toLowerCase().trim() === 'true') return true;
    return false;
}

// Custom Dropdown Component with Images
const CustomDropdown = ({ 
    ingredient, 
    priceData, 
    selectedBrands, 
    expandedDropdowns, 
    isLoadingExpansion, 
    getBrandsToDisplay, 
    handleBrandChange,
    displayAmount,
    displayUnit,
    displaySpecialOption,
    onOpenChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;
    useEffect(() => {
        onOpenChangeRef.current?.(isOpen);
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const sortedBrands = getBrandsToDisplay(ingredient.name, priceData)
        .sort((a, b) => {
            // FIRST PRIORITY: User preferences (any preference indicator)
            const aIsPreferred = a.isPreferred || a.preferenceMatch || a.isMLRecommended;
            const bIsPreferred = b.isPreferred || b.preferenceMatch || b.isMLRecommended;
            
            if (aIsPreferred && !bIsPreferred) return -1;
            if (!aIsPreferred && bIsPreferred) return 1;
            
            // SECOND PRIORITY: Special offers (after preferences)
            if (a.hasSpecial && !b.hasSpecial) return -1;
            if (!a.hasSpecial && b.hasSpecial) return 1;
            
            // Among special items, prioritize SPECIAL_PRICE over MULTI_BUY
            if (a.hasSpecial && b.hasSpecial) {
                if (a.specialType === 'SPECIAL_PRICE' && b.specialType !== 'SPECIAL_PRICE') return -1;
                if (a.specialType !== 'SPECIAL_PRICE' && b.specialType === 'SPECIAL_PRICE') return 1;
            }
            
            // THIRD PRIORITY: For per kg items, sort by price per kg
            if (a.baseUnit === 'R/kg' && b.baseUnit === 'R/kg') {
                return Number(a.costPerUnit) - Number(b.costPerUnit);
            }
            
            // FOURTH PRIORITY: Minimize excess (prefer packages that match recipe needs)
            if (a.excess !== b.excess) {
                return parseFloat(a.excess) - parseFloat(b.excess);
            }
            
            // FINAL PRIORITY: Sort by cost (lowest first)
            return Number(a.cost) - Number(b.cost);
        });

    const selectedBrand = sortedBrands.find(b => b.id === selectedBrands[ingredient.name]);
    const getPackageCount = (brand) => {
        if (!brand) return null;

        const isKgBased = brand.baseUnit && (
            brand.baseUnit.toLowerCase().includes('per kg') ||
            brand.baseUnit.toLowerCase().includes('r/kg') ||
            brand.baseUnit.toLowerCase() === 'kg'
        );
        if (isKgBased) return null;

        let packageSizeValue = 0;
        let packageSizeUnit = 'g';

        if (brand.unitEquivalent) {
            packageSizeValue = parseFloat(brand.unitEquivalent.amount) || 0;
            packageSizeUnit = brand.unitEquivalent.unit || 'g';
        } else if (brand.packageInfo) {
            const match = brand.packageInfo.match(/(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
            if (match) {
                packageSizeValue = parseFloat(match[1]);
                packageSizeUnit = match[2];
            }
        }

        if (packageSizeValue <= 0) return null;

        const ingredientAmount = parseFloat(displayAmount) || 0;
        let ingredientAmountInGrams = ingredientAmount;
        let packageSizeInGrams = packageSizeValue;

        const ingredientUnit = (displayUnit || '').toLowerCase();
        if (ingredientUnit === 'kg') ingredientAmountInGrams = ingredientAmount * 1000;
        else if (ingredientUnit === 'tsp' || ingredientUnit === 'teaspoon') ingredientAmountInGrams = ingredientAmount * 5;
        else if (ingredientUnit === 'tbsp' || ingredientUnit === 'tablespoon') ingredientAmountInGrams = ingredientAmount * 15;
        else if (ingredientUnit === 'cup' || ingredientUnit === 'cups') ingredientAmountInGrams = ingredientAmount * 250;
        else if (ingredientUnit === 'ml') ingredientAmountInGrams = ingredientAmount;
        else if (ingredientUnit === 'l') ingredientAmountInGrams = ingredientAmount * 1000;

        const packageUnitLower = packageSizeUnit.toLowerCase();
        if (packageUnitLower === 'kg') packageSizeInGrams = packageSizeValue * 1000;
        else if (packageUnitLower === 'l') packageSizeInGrams = packageSizeValue * 1000;
        else if (packageUnitLower === 'ml') packageSizeInGrams = packageSizeValue;

        let packagesNeeded = Math.max(1, Math.ceil(ingredientAmountInGrams / packageSizeInGrams));

        const userChoiceIsSpecial = displaySpecialOption?.[ingredient.name] === 'special';
        if (brand.hasSpecial && brand.specialType === 'MULTI_BUY') {
            if (userChoiceIsSpecial && brand.specialOption) {
                packagesNeeded = brand.specialOption.totalPackagesPurchased || packagesNeeded;
            } else if (brand.specialOption?.minimalOption) {
                packagesNeeded = brand.specialOption.minimalOption.packagesNeeded || packagesNeeded;
            }
        }

        return packagesNeeded;
    };

    const getBrandNameWithPacketCount = (brand) => {
        const baseName = brand?.name || 'Unknown Product';
        const packageCount = getPackageCount(brand);
        return packageCount ? `${baseName} x ${packageCount}` : baseName;
    };

    const handleOptionClick = (brandId, e) => {
        e.stopPropagation();
        handleBrandChange(ingredient.name, brandId);
        setIsOpen(false);
    };

    return (
        <div 
            className={`custom-dropdown mobile-compact ${isOpen ? 'open' : ''} ${isLoadingExpansion[ingredient.name] ? 'loading-expansion' : ''} ${expandedDropdowns[ingredient.name] ? 'expanded' : ''}`}
            ref={dropdownRef}
        >
            <div 
                className="dropdown-header mobile-dropdown-header"
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isLoadingExpansion[ingredient.name]) {
                        setIsOpen(!isOpen);
                    }
                }}
                disabled={isLoadingExpansion[ingredient.name]}
            >
                <div className="mobile-header-content">
                    {isVitalityBrand(selectedBrand) && (
                        <img
                            src={vitalityBadgeImg}
                            alt=""
                            className="vitality-badge-corner vitality-badge-corner--header"
                            title="Discovery Vitality product"
                        />
                    )}
                    <div className="mobile-brand-image-container">
                        {selectedBrand?.imageUrl ? (
                            <img
                                src={selectedBrand.imageUrl}
                                alt={`${selectedBrand.name || selectedBrand.brand}`}
                                className="mobile-brand-image"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div 
                            className="mobile-brand-image-placeholder"
                            style={{ display: selectedBrand?.imageUrl ? 'none' : 'flex' }}
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                        </div>
                    </div>
                    <div className="mobile-brand-info">
                        <h5 className="mobile-brand-name">{selectedBrand ? getBrandNameWithPacketCount(selectedBrand) : 'Select a brand...'}</h5>
                        <p className="mobile-brand-store">{selectedBrand?.brand || ''}</p>
                        {selectedBrand && (
                            <div className="mobile-brand-price">
                                {selectedBrand.hasSpecial && selectedBrand.specialType === 'SPECIAL_PRICE' && selectedBrand.originalCost ? (
                                    <>
                                        <span className="mobile-original-price">R{parseFloat(selectedBrand.originalCost).toFixed(2)}</span>
                                        <span className="mobile-special-price">R{parseFloat(selectedBrand.cost).toFixed(2)}</span>
                                    </>
                                ) : selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY' && selectedBrand.originalCost ? (
                                    <>
                                        <span className="mobile-original-price">R{parseFloat(selectedBrand.originalCost).toFixed(2)}</span>
                                        <span className="mobile-special-price">R{parseFloat(selectedBrand.cost).toFixed(2)}</span>
                                        {selectedBrand.specialOfferInfo && (
                                            <span className="mobile-special-badge">Special Deal</span>
                                        )}
                                    </>
                                ) : (
                                    <span>R{parseFloat(selectedBrand.cost).toFixed(2)}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <span className={`mobile-dropdown-arrow ${isOpen ? 'up' : 'down'}`}>▼</span>
                </div>
            </div>
            
            {isOpen && (
                <div className="mobile-dropdown-options">
                    {sortedBrands.map((brand, brandIndex) => {
                        const displayName = getBrandNameWithPacketCount(brand);
                        const brandName = brand.brand || 'Unknown Brand';
                        return (
                            <div 
                                key={brand.id}
                                className={`mobile-dropdown-option ${selectedBrands[ingredient.name] === brand.id ? 'selected' : ''}`}
                                onClick={(e) => handleOptionClick(brand.id, e)}
                            >
                                {isVitalityBrand(brand) && (
                                    <img
                                        src={vitalityBadgeImg}
                                        alt=""
                                        className="vitality-badge-corner"
                                        title="Discovery Vitality product"
                                    />
                                )}
                                <div className="mobile-option-image">
                                    {brand.imageUrl ? (
                                        <img
                                            src={brand.imageUrl}
                                            alt={`${displayName}`}
                                            className="mobile-option-brand-image"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <div 
                                        className="mobile-option-image-placeholder"
                                        style={{ display: brand.imageUrl ? 'none' : 'flex' }}
                                    >
                                        <div className="mobile-placeholder-icon">📦</div>
                                    </div>
                                </div>
                                <div className="mobile-option-details">
                                    <div className="mobile-option-name">{displayName}</div>
                                    <div className="mobile-option-brand">{brandName}</div>
                                    <div className="mobile-option-price">R{parseFloat(brand.cost).toFixed(2)}</div>
                                    <div className="mobile-option-badges">
                                        {brand.isPreferred && <span className="mobile-badge preference">⭐</span>}
                                        {brand.preferenceMatch && <span className="mobile-badge saved">💾</span>}
                                        {brand.hasSpecial && brand.specialType === 'SPECIAL_PRICE' && <span className="mobile-badge special-price">🎉</span>}
                                        {brand.hasSpecial && brand.specialType === 'MULTI_BUY' && <span className="mobile-badge multi-buy">📦</span>}
                                        {brand.hasSpecial && !brand.specialType && <span className="mobile-badge special">⚡</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    
                    {/* Show "See All" option if there are more options available and not expanded yet */}
                    {priceData.hasMoreOptions && !expandedDropdowns[ingredient.name] && (
                        <div 
                            className="mobile-dropdown-option mobile-special-option see-all"
                            onClick={(e) => handleOptionClick('see-all', e)}
                        >
                            <div className="mobile-option-image">
                                <div className="mobile-option-image-placeholder">
                                    <div className="mobile-placeholder-icon">📄</div>
                                </div>
                            </div>
                            <div className="mobile-option-details">
                                <div className="mobile-option-name">See All {priceData.totalMatches} Options...</div>
                            </div>
                        </div>
                    )}
                    
                    {/* Show "Show Less" option if currently expanded */}
                    {expandedDropdowns[ingredient.name] && (
                        <div 
                            className="mobile-dropdown-option mobile-special-option show-less"
                            onClick={(e) => handleOptionClick('show-less', e)}
                        >
                            <div className="mobile-option-image">
                                <div className="mobile-option-image-placeholder">
                                    <div className="mobile-placeholder-icon">📄</div>
                                </div>
                            </div>
                            <div className="mobile-option-details">
                                <div className="mobile-option-name">Show Less (Top 10 Only)</div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function RecipePriceDetails({ ingredients, ingredientPrices, onTotalCostChange, onRequireSignIn }) {
    const recipeId = typeof window !== 'undefined' ? window.location.pathname.split('/').pop() : null;
    console.log('🚀 RecipePriceDetails component rendered with props:', { 
        ingredients: ingredients?.length, 
        ingredientPrices: ingredientPrices ? Object.keys(ingredientPrices).length : 0 
    });
    
    const navigate = useNavigate();
    const [cookies] = useCookies(['access_token']);
    
    // Token validation state
    const [tokenValidated, setTokenValidated] = useState(false);
    const [tokenValidationAttempted, setTokenValidationAttempted] = useState(false);
    
    const [selectedBrands, setSelectedBrands] = useState({});
    const [selectedIngredients, setSelectedIngredients] = useState({});
    const [displaySpecialOption, setDisplaySpecialOption] = useState({});
    const [totalCost, setTotalCost] = useState(0);
    const [originalTotal, setOriginalTotal] = useState(0);
    const [totalSavings, setTotalSavings] = useState(0);
    // Removed search functionality - no longer needed
    const [cupboardIngredients, setCupboardIngredients] = useState([]);
    const [expandedDropdowns, setExpandedDropdowns] = useState({}); // Track which dropdowns are expanded
    const [expandedOptions, setExpandedOptions] = useState({}); // Store expanded options for ingredients
    const [isLoadingExpansion, setIsLoadingExpansion] = useState({}); // Track loading state for expansions

    // New state for the deselect popup
    const [showDeselectPopup, setShowDeselectPopup] = useState(false);
    const [deselectIngredient, setDeselectIngredient] = useState(null);
    const [isAddingToCupboard, setIsAddingToCupboard] = useState(false);
    /** Which ingredient's brand dropdown is open — lifts card z-index so the dropdown isn't covered by sibling cards */
    const [openDropdownIngredientName, setOpenDropdownIngredientName] = useState(null);

    // Helper to get auth token from cookies or localStorage
    const getAuthToken = () => cookies.access_token || window.localStorage.getItem('auth_token');

    // Helper to build Authorization header only when token exists
    const getAuthHeaders = () => {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // Token validation effect - similar to CreateRecipe.js
    useEffect(() => {
        const validateToken = () => {
            // Check both cookie and localStorage for a token
            const cookieToken = cookies.access_token;
            const localStorageToken = localStorage.getItem('auth_token');
            const hasToken = !!cookieToken || !!localStorageToken;

            console.log('RecipePriceDetails token validation check:', {
                inCookie: !!cookieToken,
                inLocalStorage: !!localStorageToken,
                hasToken
            });

            if (!hasToken) {
                console.log('No auth token available for RecipePriceDetails');
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                // Don't navigate to auth - allow viewing prices without login
                return;
            }

            // Get the token to validate (prefer cookie, fallback to localStorage)
            const token = cookieToken || localStorageToken;
            
            try {
                // Manually validate the token by decoding and checking expiration
                const parts = token.split('.');
                if (parts.length !== 3) {
                    throw new Error('Invalid token format');
                }
                
                // Decode the payload
                const payload = JSON.parse(atob(parts[1]));
                const now = Math.floor(Date.now() / 1000);
                
                // Check if token is expired
                if (payload.exp && payload.exp < now) {
                    console.log('Token has expired in RecipePriceDetails');
                    setTokenValidated(false);
                    setTokenValidationAttempted(true);
                    // Clear expired token data
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('userID');
                    return;
                }
                
                console.log('RecipePriceDetails token validated successfully');
                setTokenValidated(true);
                setTokenValidationAttempted(true);
                
            } catch (tokenError) {
                console.error('Token validation failed in RecipePriceDetails:', tokenError);
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                // Clear invalid token data
                localStorage.removeItem('auth_token');
                localStorage.removeItem('userID');
            }
        };

        // Run validation immediately
        validateToken();
        
    }, [cookies.access_token]);

    // Listen for auth token updates from login
    useEffect(() => {
        const handleAuthTokenUpdate = (event) => {
            console.log('RecipePriceDetails: Auth token updated, refreshing auth state');
            // Force re-validation of the new token
            setTokenValidated(false);
            setTokenValidationAttempted(false);
        };

        window.addEventListener('auth-token-updated', handleAuthTokenUpdate);
        
        return () => {
            window.removeEventListener('auth-token-updated', handleAuthTokenUpdate);
        };
    }, []);

    // Prevent body scroll when popup is open on mobile
    useEffect(() => {
        if (showDeselectPopup) {
            // Check if mobile (window width <= 768px)
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
            }
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [showDeselectPopup]);

    useEffect(() => {
        console.log('=== FRONTEND DEBUG: RecipePriceDetails useEffect ===');
        console.log('Received from backend:', {
            ingredientPrices,
            ingredients
        });
        
        // Debug the structure of ingredientPrices
        console.log('ingredientPrices type:', typeof ingredientPrices);
        console.log('ingredientPrices keys:', Object.keys(ingredientPrices || {}));
        
        if (ingredientPrices && Object.keys(ingredientPrices).length > 0) {
            console.log('First ingredient sample:', {
                key: Object.keys(ingredientPrices)[0],
                value: ingredientPrices[Object.keys(ingredientPrices)[0]]
            });
            
            // Log each ingredient's data structure for debugging
            Object.entries(ingredientPrices).forEach(([key, value]) => {
                console.log(`Ingredient "${key}":`, value);
                if (value?.selectedBrand) {
                    console.log(`  - Has selectedBrand: ${value.selectedBrand.name}`);
                } else if (value?.availableBrands?.length > 0) {
                    console.log(`  - No selectedBrand, but has ${value.availableBrands.length} available brands`);
                } else {
                    console.log(`  - No selectedBrand or availableBrands`);
                }
            });
        }

        async function initializeWithCupboard() {
            try {
                const initialBrands = {};
                const initialSelected = {};
                const initialDisplaySpecialOption = {};
                let newTotalCost = 0;
                let newOriginalTotal = 0;
                let newTotalSavings = 0;

                // Check cupboard for existing ingredients - only if token is validated
                let cupboardIngredients = [];
                if (tokenValidated && getAuthToken()) {
                    try {
                        const userID = window.localStorage.getItem('userID');
                        if (userID) {
                            const cupboardResponse = await axios.get(`${API_BASE_URL}/cupboard/${userID}`, {
                                headers: getAuthHeaders()
                            });
                            
                            if (cupboardResponse.data.success) {
                                cupboardIngredients = cupboardResponse.data.items;
                                setCupboardIngredients(cupboardIngredients);
                                console.log('Found cupboard ingredients:', cupboardIngredients.map(item => item.ingredientName));
                            }
                        }
                    } catch (error) {
                        console.log('Could not fetch cupboard items (user may not be logged in):', error.message);
                    }
                }

                Object.entries(ingredientPrices || {}).forEach(([ingredientName, data]) => {
                    console.log(`Processing ingredient in frontend: ${ingredientName}`, data);
                    
                    // Check if this ingredient exists in cupboard
                    const inCupboard = cupboardIngredients.some(cupboardItem => 
                        cupboardItem.ingredientName.toLowerCase() === ingredientName.toLowerCase()
                    );
                    
                    if (inCupboard) {
                        console.log(`🏠 ${ingredientName} found in cupboard - will be unselected`);
                    }
                    
                    if (data && data.selectedBrand) {
                        console.log(`✅ Found selectedBrand for ${ingredientName}:`, data.selectedBrand);
                        initialSelected[ingredientName] = !inCupboard; // Auto-unselect if in cupboard
                        initialBrands[ingredientName] = data.selectedBrand.id;
                        
                        // Set initial display: use backend user choice if available, otherwise prioritize special items
                        if (data.selectedBrand.userChoiceApplied) {
                            // Use the backend applied user choice
                            initialDisplaySpecialOption[ingredientName] = data.selectedBrand.userChoiceApplied;
                            console.log(`✅ Using backend user choice for ${ingredientName}: ${data.selectedBrand.userChoiceApplied}`);
                        } else if (data.selectedBrand.hasSpecial && data.selectedBrand.specialType === 'MULTI_BUY') {
                            // Default to recommended option for multi-buy items
                            const recommendedOption = data.selectedBrand.savings > 0 ? 'special' : 'minimal';
                            initialDisplaySpecialOption[ingredientName] = recommendedOption;
                            console.log(`📋 Setting default display option for ${ingredientName}: ${recommendedOption}`);
                        } else if (data.selectedBrand.hasSpecial) {
                            // Default to special if no saved choice and has special
                            initialDisplaySpecialOption[ingredientName] = 'special';
                        } else {
                            initialDisplaySpecialOption[ingredientName] = 'single';
                        }

                        // Only add to cost if not in cupboard
                        if (!inCupboard) {
                            newTotalCost += Number(data.selectedBrand.cost);
                            newOriginalTotal += Number(data.selectedBrand.originalCost);
                        }
                    } else if (data && data.availableBrands && data.availableBrands.length > 0) {
                        // If no selectedBrand but we have available brands, auto-select the first one
                        console.log(`⚠️ No selectedBrand for ${ingredientName}, but found ${data.availableBrands.length} available brands. Auto-selecting first one.`);
                        const firstBrand = data.availableBrands[0];
                        initialSelected[ingredientName] = !inCupboard; // Auto-unselect if in cupboard
                        initialBrands[ingredientName] = firstBrand.id;
                        // Set initial display: use backend user choice if available, otherwise prioritize special items
                        if (firstBrand.userChoiceApplied) {
                            // Use the backend applied user choice
                            initialDisplaySpecialOption[ingredientName] = firstBrand.userChoiceApplied;
                            console.log(`✅ Using backend user choice for ${ingredientName}: ${firstBrand.userChoiceApplied}`);
                        } else if (firstBrand.hasSpecial && firstBrand.specialType === 'MULTI_BUY') {
                            // Default to recommended option for multi-buy items
                            const recommendedOption = firstBrand.savings > 0 ? 'special' : 'minimal';
                            initialDisplaySpecialOption[ingredientName] = recommendedOption;
                            console.log(`📋 Setting default display option for ${ingredientName}: ${recommendedOption}`);
                        } else if (firstBrand.hasSpecial) {
                            // Default to special if no saved choice and has special
                            initialDisplaySpecialOption[ingredientName] = 'special';
                        } else {
                            initialDisplaySpecialOption[ingredientName] = 'single';
                        }
                        
                        // Only add to cost if not in cupboard
                        if (!inCupboard) {
                            newTotalCost += Number(firstBrand.cost);
                            newOriginalTotal += Number(firstBrand.originalCost);
                        }
                    } else {
                        console.log(`❌ No selectedBrand or availableBrands for ${ingredientName}. Data structure:`, data);
                    }
                });

                // Search functionality removed

                // Calculate total savings correctly: originalTotal - finalTotal
                newTotalSavings = newOriginalTotal - newTotalCost;

                console.log('Setting initial state:');
                console.log('  - initialSelected:', initialSelected);
                console.log('  - initialBrands:', initialBrands);
                console.log('  - newTotalCost:', newTotalCost);
                console.log('  - newOriginalTotal:', newOriginalTotal);
                console.log('  - newTotalSavings:', newTotalSavings);
                console.log('  - calculatedSavings:', `${newOriginalTotal} - ${newTotalCost} = ${newTotalSavings}`);

                setSelectedBrands(initialBrands);
                setSelectedIngredients(initialSelected);
                setDisplaySpecialOption(initialDisplaySpecialOption);
                setTotalCost(newTotalCost);
                setOriginalTotal(newOriginalTotal);
                setTotalSavings(newTotalSavings);

            } catch (error) {
                console.error('Error processing ingredient prices:', error);
            }
        }

        // Only initialize if token validation is complete
        if (tokenValidationAttempted) {
            initializeWithCupboard();
        }
    }, [ingredientPrices, tokenValidated, tokenValidationAttempted]);

    // Recalculate totals whenever ingredient prices change (after backend updates)
    useEffect(() => {
        if (ingredientPrices && Object.keys(ingredientPrices).length > 0) {
            let newTotalCost = 0;
            let newOriginalTotal = 0;

            Object.entries(ingredientPrices).forEach(([name, data]) => {
                if (selectedIngredients[name]) {
                    const selectedBrandId = selectedBrands[name];
                    const selectedBrand = data.availableBrands.find(b => b.id === selectedBrandId);
                    
                    if (selectedBrand) {
                        // Apply the user's choice to get the correct cost
                        const userChoice = displaySpecialOption[name];
                        let costToUse = selectedBrand.cost;
                        let originalCostToUse = selectedBrand.originalCost || selectedBrand.cost;
                        
                        // For multi-buy items, apply the user's choice
                        if (selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY') {
                            if (userChoice === 'special' && selectedBrand.specialOption) {
                                costToUse = parseFloat(selectedBrand.specialOption.totalCost);
                                originalCostToUse = parseFloat(selectedBrand.specialOption.regularCostForSameAmount);
                            } else if (selectedBrand.specialOption?.minimalOption) {
                                costToUse = parseFloat(selectedBrand.specialOption.minimalOption.totalCost);
                                originalCostToUse = parseFloat(selectedBrand.specialOption.minimalOption.totalCost);
                            }
                        }
                        
                        newTotalCost += costToUse;
                        newOriginalTotal += originalCostToUse;
                    }
                }
            });

            // Calculate total savings correctly: originalTotal - finalTotal
            const newTotalSavings = newOriginalTotal - newTotalCost;

            setTotalCost(newTotalCost);
            setOriginalTotal(newOriginalTotal);
            setTotalSavings(newTotalSavings);
            
            console.log('📊 Totals updated after ingredient prices change:', {
                totalCost: newTotalCost,
                originalTotal: newOriginalTotal,
                totalSavings: newTotalSavings,
                calculatedSavings: `${newOriginalTotal} - ${newTotalCost} = ${newTotalSavings}`
            });
        }
    }, [ingredientPrices, selectedIngredients, selectedBrands, displaySpecialOption]);

    // Notify parent component when total cost changes
    useEffect(() => {
        if (onTotalCostChange && typeof onTotalCostChange === 'function') {
            onTotalCostChange(totalCost);
        }
    }, [totalCost, onTotalCostChange]);

    // Search functionality removed

  

    const handleBrandChange = async (ingredientName, brandId) => {
        console.log(`Brand change - Ingredient: ${ingredientName}, New brand ID: ${brandId}`);
        
        // Handle special dropdown expansion/collapse options
        if (brandId === 'see-all') {
            await handleExpandDropdown(ingredientName);
            return; // Don't change the actual selection
        } else if (brandId === 'show-less') {
            setExpandedDropdowns(prev => ({
                ...prev,
                [ingredientName]: false
            }));
            return; // Don't change the actual selection
        }
        
        const newSelectedBrands = { ...selectedBrands };
        
        if (brandId === 'none') {
            delete newSelectedBrands[ingredientName];
            console.log(`Removed brand selection for ${ingredientName}`);
        } else {
            newSelectedBrands[ingredientName] = brandId;
            console.log(`Set brand selection for ${ingredientName} to ${brandId}`);
            
            // Log the selected brand details - look in both available brands and expanded options
            const priceData = ingredientPrices[ingredientName];
            const brandsToSearch = expandedDropdowns[ingredientName] && expandedOptions[ingredientName] 
                ? expandedOptions[ingredientName] 
                : priceData?.availableBrands || [];
            
            const selectedBrand = brandsToSearch.find(b => b.id === brandId);
            if (selectedBrand) {
                console.log(`Selected brand details: ${selectedBrand.name} (${selectedBrand.brand})`);
                
                // Log for machine learning improvement
                logIngredientMatch(ingredientName, selectedBrand);
                
                // Automatically save this preference for the user
                saveIndividualPreference(ingredientName, selectedBrand);
            } else {
                console.log(`Warning: Could not find brand with ID ${brandId} in available brands`);
            }
        }

        // Update state IMMEDIATELY
        setSelectedBrands(newSelectedBrands);
        
        // IMMEDIATE frontend calculation
        let immediateTotalCost = 0;
        let immediateOriginalTotal = 0;

        Object.entries(ingredientPrices).forEach(([name, data]) => {
            if (selectedIngredients[name]) {
                const selectedBrandId = newSelectedBrands[name] || selectedBrands[name];
                const selectedBrand = data.availableBrands.find(b => b.id === selectedBrandId);
                
                if (selectedBrand) {
                    // Apply the user's choice to get the correct cost
                    const userChoice = displaySpecialOption[name];
                    let costToUse = parseFloat(selectedBrand.cost) || 0;
                    let originalCostToUse = parseFloat(selectedBrand.originalCost || selectedBrand.cost) || 0;
                    
                    // For multi-buy items, apply the user's choice
                    if (selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY') {
                        if (userChoice === 'special' && selectedBrand.specialOption) {
                            costToUse = parseFloat(selectedBrand.specialOption.totalCost) || costToUse;
                            originalCostToUse = parseFloat(selectedBrand.specialOption.regularCostForSameAmount) || originalCostToUse;
                        } else if (userChoice === 'minimal' && selectedBrand.specialOption?.minimalOption) {
                            costToUse = parseFloat(selectedBrand.specialOption.minimalOption.totalCost) || costToUse;
                            originalCostToUse = parseFloat(selectedBrand.specialOption.minimalOption.totalCost) || originalCostToUse;
                        }
                    }
                    
                    immediateTotalCost += costToUse;
                    immediateOriginalTotal += originalCostToUse;
                }
            }
        });

        // Calculate total savings correctly: originalTotal - finalTotal
        const immediateTotalSavings = immediateOriginalTotal - immediateTotalCost;

        // Update totals IMMEDIATELY
        setTotalCost(immediateTotalCost);
        setOriginalTotal(immediateOriginalTotal);
        setTotalSavings(immediateTotalSavings);
        
        console.log('⚡ IMMEDIATE brand change totals update:', {
            totalCost: immediateTotalCost,
            originalTotal: immediateOriginalTotal,
            totalSavings: immediateTotalSavings,
            changedIngredient: ingredientName,
            newBrandId: brandId
        });
        
        // Background backend update (non-blocking)
        setTimeout(async () => {
            try {
                const userId = window.localStorage.getItem('userID');
                console.log('🔄 Background backend recalculation for brand change...');
                
                const response = await axios.post(`${API_BASE_URL}/recipes/${recipeId}/calculate-cost`, {
                    ingredients: ingredients,
                    userId: userId,
                    preferredRegion: window.localStorage.getItem('preferredRegion') || null,
                    updateUserChoices: true
                });
                
                if (response.data.ingredientPrices) {
                    console.log('✅ Background backend update completed for brand change');
                    console.log('Updated ingredient prices from backend:', response.data.ingredientPrices);
                    console.log('🔎 calculate-cost debug:', {
                        debugRegion: response.data?.debugRegion,
                        debugCollection: response.data?.debugCollection,
                        debugRegionSource: response.data?.debugRegionSource,
                        debugRequestPreferredRegion: response.data?.debugRequestPreferredRegion,
                        debugUserPreferredRegion: response.data?.debugUserPreferredRegion
                    });
                }
            } catch (error) {
                console.error('❌ Background backend update failed for brand change:', error);
            }
        }, 1000);
    };

    // Save individual preference when user changes a selection
    const saveIndividualPreference = async (ingredientName, selectedBrand) => {
        if (!tokenValidated || !getAuthToken()) {
            console.log('User not logged in or token invalid, skipping preference save');
            return;
        }

        try {
            const userId = window.localStorage.getItem('userID');
            if (!userId) {
                console.log('No user ID found, skipping preference save');
                return;
            }

            const preference = {
                recipeIngredient: ingredientName,
                dbIngredient: {
                    ingredientId: selectedBrand.id,
                    name: selectedBrand.name,
                    brand: selectedBrand.brand || ''
                }
            };

            console.log(`💾 Auto-saving preference: ${ingredientName} → ${selectedBrand.name}`);

            const response = await axios.post(
                `${API_BASE_URL}/recipes/save-single-preference`,
                {
                    userId,
                    preference
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    }
                }
            );

            if (response.data.success) {
                console.log(`✅ Preference saved successfully for ${ingredientName}`);
            } else {
                console.log(`⚠️ Failed to save preference for ${ingredientName}:`, response.data.message);
            }
        } catch (error) {
            console.error(`❌ Error saving preference for ${ingredientName}:`, error);
        }
    };



    // Log selected ingredient for machine learning improvement
    const logIngredientMatch = async (recipeIngredient, selectedBrand) => {
        try {
            await axios.post(`${API_BASE_URL}/ingredients/log-match`, {
                recipeIngredient,
                selectedBrandId: selectedBrand.id,
                selectedBrandName: selectedBrand.name,
                isUserSelected: true // Flag that this was a user selection
            });
            console.log(`Logged ingredient match for ML improvement: ${recipeIngredient} → ${selectedBrand.name}`);
        } catch (error) {
            console.error('Error logging ingredient match:', error);
        }
    };

    // Search functionality removed

    // Search functionality removed

    // Search functionality removed

    const handleIngredientToggle = (ingredientName) => {
        // If we're deselecting an ingredient, show the popup
        if (selectedIngredients[ingredientName]) {
            const ingredient = ingredients.find(ing => ing.name === ingredientName);
            setDeselectIngredient(ingredient);
            setShowDeselectPopup(true);
            return;
        }

        // If we're selecting an ingredient, proceed normally
        const newSelectedIngredients = {
            ...selectedIngredients,
            [ingredientName]: !selectedIngredients[ingredientName]
        };

        const newSelectedBrands = { ...selectedBrands };
        if (!newSelectedIngredients[ingredientName]) {
            delete newSelectedBrands[ingredientName];
        } else {
            const priceData = ingredientPrices[ingredientName];
            if (priceData?.selectedBrand) {
                newSelectedBrands[ingredientName] = priceData.selectedBrand.id;
            }
        }

        // Update the states
        setSelectedIngredients(newSelectedIngredients);
        setSelectedBrands(newSelectedBrands);
        
        // Recalculate totals immediately with current ingredient prices
        let newTotalCost = 0;
        let newOriginalTotal = 0;

        Object.entries(ingredientPrices).forEach(([name, data]) => {
            if (newSelectedIngredients[name]) {
                const selectedBrandId = newSelectedBrands[name];
                const selectedBrand = data.availableBrands.find(b => b.id === selectedBrandId);
                
                if (selectedBrand) {
                    // Use the current backend calculated values
                    newTotalCost += parseFloat(selectedBrand.cost) || 0;
                    newOriginalTotal += parseFloat(selectedBrand.originalCost || selectedBrand.cost) || 0;
                }
            }
        });

        // Calculate total savings correctly: originalTotal - finalTotal
        const newTotalSavings = newOriginalTotal - newTotalCost;

        setTotalCost(newTotalCost);
        setOriginalTotal(newOriginalTotal);
        setTotalSavings(newTotalSavings);
        
        console.log('📊 Updated totals after ingredient toggle:', {
            totalCost: newTotalCost,
            originalTotal: newOriginalTotal,
            totalSavings: newTotalSavings
        });
    };

    // Handle deselect with cupboard option
    const handleDeselectWithCupboard = async () => {
        if (!deselectIngredient) return;

        setIsAddingToCupboard(true);
        
        try {
            // Add to cupboard first
            if (tokenValidated && getAuthToken()) {
                const userId = window.localStorage.getItem('userID');
                if (userId) {
                    await axios.post(`${API_BASE_URL}/cupboard/add`, {
                        ingredientName: deselectIngredient.name,
                        amount: deselectIngredient.amount,
                        unit: deselectIngredient.unit
                    }, {
                        headers: getAuthHeaders()
                    });
                    
                    // Refresh cupboard ingredients
                    const cupboardResponse = await axios.get(`${API_BASE_URL}/cupboard/${userId}`, {
                        headers: getAuthHeaders()
                    });
                    
                    if (cupboardResponse.data.success) {
                        setCupboardIngredients(cupboardResponse.data.items);
                    }
                }
            }

            // Then deselect the ingredient
            const newSelectedIngredients = {
                ...selectedIngredients,
                [deselectIngredient.name]: false
            };

            const newSelectedBrands = { ...selectedBrands };
            delete newSelectedBrands[deselectIngredient.name];

            setSelectedIngredients(newSelectedIngredients);
            setSelectedBrands(newSelectedBrands);
            
            // Recalculate totals
            let newTotalCost = 0;
            let newOriginalTotal = 0;

            Object.entries(ingredientPrices).forEach(([name, data]) => {
                if (newSelectedIngredients[name]) {
                    const selectedBrandId = newSelectedBrands[name];
                    const selectedBrand = data.availableBrands.find(b => b.id === selectedBrandId);
                    
                    if (selectedBrand) {
                        newTotalCost += parseFloat(selectedBrand.cost) || 0;
                        newOriginalTotal += parseFloat(selectedBrand.originalCost || selectedBrand.cost) || 0;
                    }
                }
            });

            // Calculate total savings correctly: originalTotal - finalTotal
            const newTotalSavings = newOriginalTotal - newTotalCost;

            setTotalCost(newTotalCost);
            setOriginalTotal(newOriginalTotal);
            setTotalSavings(newTotalSavings);
            
        } catch (error) {
            console.error('Error adding to cupboard:', error);
        } finally {
            setIsAddingToCupboard(false);
            setShowDeselectPopup(false);
            setDeselectIngredient(null);
        }
    };

    // Add single ingredient to cupboard from card (no popup). Deselects so ingredient moves to bottom.
    const handleAddToCupboard = async (ingredient) => {
        if (!ingredient) return;
        if (!tokenValidated || !getAuthToken()) {
            if (onRequireSignIn && typeof onRequireSignIn === 'function') {
                onRequireSignIn();
            } else {
                alert('Please log in to add ingredients to your cupboard.');
            }
            return;
        }
        const userId = window.localStorage.getItem('userID');
        if (!userId) return;
        // Normalize amount/unit for consolidated ingredients (e.g. amounts array)
        const amount = ingredient.amount ?? ingredient.amounts?.[0]?.amount ?? 0;
        const unit = ingredient.unit ?? ingredient.amounts?.[0]?.unit ?? '';
        setIsAddingToCupboard(true);
        try {
            await axios.post(`${API_BASE_URL}/cupboard/add`, {
                ingredientName: ingredient.name,
                amount,
                unit
            }, { headers: getAuthHeaders() });
            const cupboardResponse = await axios.get(`${API_BASE_URL}/cupboard/${userId}`, { headers: getAuthHeaders() });
            if (cupboardResponse.data.success) {
                setCupboardIngredients(cupboardResponse.data.items);
            }
            // Deselect so ingredient disappears from list and appears at bottom (like popup "Add to Cupboard & Deselect")
            const newSelected = { ...selectedIngredients, [ingredient.name]: false };
            const newBrands = { ...selectedBrands };
            delete newBrands[ingredient.name];
            setSelectedIngredients(newSelected);
            setSelectedBrands(newBrands);
            let newTotalCost = 0, newOriginalTotal = 0;
            Object.entries(ingredientPrices).forEach(([name, data]) => {
                if (newSelected[name]) {
                    const bid = newBrands[name];
                    const brand = data?.availableBrands?.find(b => b.id === bid);
                    if (brand) {
                        newTotalCost += parseFloat(brand.cost) || 0;
                        newOriginalTotal += parseFloat(brand.originalCost || brand.cost) || 0;
                    }
                }
            });
            setTotalCost(newTotalCost);
            setOriginalTotal(newOriginalTotal);
            setTotalSavings(newOriginalTotal - newTotalCost);
        } catch (error) {
            console.error('Error adding to cupboard:', error);
        } finally {
            setIsAddingToCupboard(false);
        }
    };

    // Handle deselect without cupboard
    const handleDeselectOnly = () => {
        if (!deselectIngredient) return;

        const newSelectedIngredients = {
            ...selectedIngredients,
            [deselectIngredient.name]: false
        };

        const newSelectedBrands = { ...selectedBrands };
        delete newSelectedBrands[deselectIngredient.name];

        setSelectedIngredients(newSelectedIngredients);
        setSelectedBrands(newSelectedBrands);
        
        // Recalculate totals
        let newTotalCost = 0;
        let newOriginalTotal = 0;

        Object.entries(ingredientPrices).forEach(([name, data]) => {
            if (newSelectedIngredients[name]) {
                const selectedBrandId = newSelectedBrands[name];
                const selectedBrand = data.availableBrands.find(b => b.id === selectedBrandId);
                
                if (selectedBrand) {
                    newTotalCost += parseFloat(selectedBrand.cost) || 0;
                    newOriginalTotal += parseFloat(selectedBrand.originalCost || selectedBrand.cost) || 0;
                }
            }
        });

        // Calculate total savings correctly: originalTotal - finalTotal
        const newTotalSavings = newOriginalTotal - newTotalCost;

        setTotalCost(newTotalCost);
        setOriginalTotal(newOriginalTotal);
        setTotalSavings(newTotalSavings);
        
        setShowDeselectPopup(false);
        setDeselectIngredient(null);
    };

    // Cancel deselect popup
    const cancelDeselect = () => {
        setShowDeselectPopup(false);
        setDeselectIngredient(null);
    };

    // New function to handle clicks on the ingredient item (excluding dropdown)
    const handleIngredientItemClick = (event, ingredientName) => {
        // Check if the click was on the dropdown or its children
        const isDropdownClick = event.target.closest('.brand-select-container') || 
                               event.target.closest('.brand-select') ||
                               event.target.closest('.brand-selector-wrapper') ||
                            //    event.target.closest('.brand-image-container') ||
                               event.target.closest('.special-offer') ||
                               event.target.closest('.pricing-options') ||
                               event.target.closest('.option-group') ||
                               event.target.closest('.option-item') ||
                               event.target.closest('.brand-details') ||
                               event.target.closest('.ingredient-checkbox') ||
                               event.target.closest('.add-to-cupboard-row');
        
        // If it's not a dropdown click, toggle the ingredient
        if (!isDropdownClick) {
            handleIngredientToggle(ingredientName);
        }
    };

    // Handle expanding dropdown to show all available options
    const handleExpandDropdown = async (ingredientName) => {
        if (expandedDropdowns[ingredientName]) {
            // If already expanded, collapse it
            setExpandedDropdowns(prev => ({
                ...prev,
                [ingredientName]: false
            }));
            return;
        }

        // Set loading state
        setIsLoadingExpansion(prev => ({
            ...prev,
            [ingredientName]: true
        }));

        try {
            const response = await axios.post(
                `${API_BASE_URL}/recipes/expand-ingredient-options`,
                {
                    recipeId,
                    ingredientName,
                    userId: window.localStorage.getItem('userID')
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    }
                }
            );

            if (response.data.success) {
                // Store the expanded options
                setExpandedOptions(prev => ({
                    ...prev,
                    [ingredientName]: response.data.allAvailableBrands
                }));

                // Mark as expanded
                setExpandedDropdowns(prev => ({
                    ...prev,
                    [ingredientName]: true
                }));

                console.log(`✅ Expanded options loaded for ${ingredientName}: ${response.data.allAvailableBrands.length} total options`);
            } else {
                console.log(`⚠️ Failed to expand options for ${ingredientName}:`, response.data.message);
            }
        } catch (error) {
            console.error(`❌ Error expanding options for ${ingredientName}:`, error);
        } finally {
            // Clear loading state
            setIsLoadingExpansion(prev => ({
                ...prev,
                [ingredientName]: false
            }));
        }
    };

    // Get the brands to display in dropdown (either limited or expanded)
    const getBrandsToDisplay = (ingredientName, priceData) => {
        if (!priceData) return [];
        
        // If dropdown is expanded, show all available brands
        if (expandedDropdowns[ingredientName] && expandedOptions[ingredientName]) {
            return expandedOptions[ingredientName];
        }
        
        // Otherwise show the default limited list
        return priceData.availableBrands || [];
    };

    // Helper function to get ingredient by name
    const getIngredientByName = (name) => {
        return ingredients.find(ing => ing.name === name);
    };

    // Normalize amount/unit for consolidated ingredients (e.g. MealPlanDetails uses amounts array)
    const getIngredientAmountUnit = (ing) => ({
        amount: ing?.amount ?? ing?.amounts?.[0]?.amount ?? 0,
        unit: ing?.unit ?? ing?.amounts?.[0]?.unit ?? ''
    });

    // Helper function to format ingredient amount display
    // Handles fractional cups properly (e.g., 0.5 cup → "1/2 cup", not "0.5 cup")
    const formatIngredientAmount = (amount, unit) => {
        const amountNum = parseFloat(amount);
        const unitLower = (unit || '').toLowerCase().trim();
        
        // Handle decimal cup amounts
        if (unitLower === 'cup' || unitLower === 'cups') {
            if (amountNum === 0.5) {
                return '1/2 cup';
            }
            if (amountNum === 0.25) {
                return '1/4 cup';
            }
            if (amountNum === 0.75) {
                return '3/4 cup';
            }
        }
        
        // Handle cases where unit already contains fraction (e.g., "1/2cup")
        // If amount is 1 and unit contains "1/2", just show "1/2 cup"
        if (amountNum === 1) {
            if (unitLower.includes('1/2') || unitLower.includes('½')) {
                return '1/2 cup';
            }
            if (unitLower.includes('1/4') || unitLower.includes('¼')) {
                return '1/4 cup';
            }
            if (unitLower.includes('3/4') || unitLower.includes('¾')) {
                return '3/4 cup';
            }
        }
        
        // Handle fractional units that already have the fraction in the unit name
        if (unitLower === '1/2cup' || unitLower === '1/2 cup' || unitLower === '½cup' || unitLower === '½ cup') {
            // If amount is 1, just show "1/2 cup", otherwise show the amount
            if (amountNum === 1) {
                return '1/2 cup';
            }
            return `${amount} 1/2 cup`;
        }
        if (unitLower === '1/4cup' || unitLower === '1/4 cup' || unitLower === '¼cup' || unitLower === '¼ cup') {
            // If amount is 1, just show "1/4 cup", otherwise show the amount
            if (amountNum === 1) {
                return '1/4 cup';
            }
            return `${amount} 1/4 cup`;
        }
        
        // Default: show amount and unit as-is
        return `${amount} ${unit}`;
    };

    // Helper function to calculate cost for a single ingredient based on user's choice
    const calculateIngredientCost = (ingredientName, selectedBrand) => {
        if (!selectedBrand) return { cost: 0, originalCost: 0, savings: 0 };

        // Use backend calculated values directly - they should already include user choice
        return {
            cost: parseFloat(selectedBrand.cost),
            originalCost: parseFloat(selectedBrand.originalCost || selectedBrand.cost),
            savings: parseFloat(selectedBrand.savings || 0)
        };
    };

    // Function to recalculate all totals
    const recalculateTotals = (overrideDisplayOptions = null) => {
        let newTotalCost = 0;
        let newOriginalTotal = 0;

        const currentDisplayOptions = overrideDisplayOptions || displaySpecialOption;

        Object.entries(ingredientPrices).forEach(([name, data]) => {
            if (selectedIngredients[name]) {
                const selectedBrandId = selectedBrands[name];
                const selectedBrand = data.availableBrands.find(b => b.id === selectedBrandId);
                
                if (selectedBrand) {
                    // Use the same calculateIngredientCost function for consistency
                    const costs = calculateIngredientCost(name, selectedBrand);
                    
                    newTotalCost += costs.cost;
                    newOriginalTotal += costs.originalCost;
                }
            }
        });

        // Calculate total savings correctly: originalTotal - finalTotal
        const newTotalSavings = newOriginalTotal - newTotalCost;

        setTotalCost(newTotalCost);
        setOriginalTotal(newOriginalTotal);
        setTotalSavings(newTotalSavings);
        
        console.log('Totals recalculated:', {
            totalCost: newTotalCost,
            originalTotal: newOriginalTotal,
            totalSavings: newTotalSavings,
            calculatedSavings: `${newOriginalTotal} - ${newTotalCost} = ${newTotalSavings}`
        });
    };

    // Save user's choice between regular and special options to backend
    const saveOptionChoice = async (ingredientName, option, selectedBrand) => {
        if (!tokenValidated || !getAuthToken()) {
            console.log('User not logged in or token invalid, skipping option choice save');
            return;
        }

        try {
            const userId = window.localStorage.getItem('userID');
            if (!userId) {
                console.log('No user ID found, skipping option choice save');
                return;
            }

            const choiceData = {
                userId,
                ingredientName,
                brandId: selectedBrand.id,
                brandName: selectedBrand.name,
                userChoice: option, // 'minimal' or 'special'
                choiceDate: new Date().toISOString(),
                regularOption: selectedBrand.regularOption || null,
                specialOption: selectedBrand.specialOption || null
            };

            console.log(`💾 Saving user choice: ${ingredientName} → ${option}`);

            const response = await axios.post(
                `${API_BASE_URL}/recipes/save-option-choice`,
                choiceData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    }
                }
            );

            if (response.data.success) {
                console.log(`✅ Option choice saved successfully for ${ingredientName}`);
            } else {
                console.log(`⚠️ Failed to save option choice for ${ingredientName}:`, response.data.message);
            }
        } catch (error) {
            console.error(`❌ Error saving option choice for ${ingredientName}:`, error);
        }
    };

    // Handler for the option selection - DOES recalculate totals based on user choice
    const handleDisplayOptionChange = async (ingredientName, option) => {
        const newDisplayOptions = {
            ...displaySpecialOption,
            [ingredientName]: option
        };
        
        console.log(`INFO: Changed display option for ${ingredientName} to ${option}. Recalculating totals...`);
        
        // Update state IMMEDIATELY first
        setDisplaySpecialOption(newDisplayOptions);
        
        // IMMEDIATE frontend calculation - don't wait for anything
        let immediateTotalCost = 0;
        let immediateOriginalTotal = 0;

        Object.entries(ingredientPrices).forEach(([name, data]) => {
            if (selectedIngredients[name]) {
                const selectedBrandId = selectedBrands[name];
                const selectedBrand = data.availableBrands.find(b => b.id === selectedBrandId);
                
                if (selectedBrand) {
                    // Apply the user's choice to get the correct cost
                    const userChoice = newDisplayOptions[name];
                    let costToUse = parseFloat(selectedBrand.cost) || 0;
                    let originalCostToUse = parseFloat(selectedBrand.originalCost || selectedBrand.cost) || 0;
                    
                    // For multi-buy items, apply the user's choice
                    if (selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY') {
                        if (userChoice === 'special' && selectedBrand.specialOption) {
                            costToUse = parseFloat(selectedBrand.specialOption.totalCost) || costToUse;
                            originalCostToUse = parseFloat(selectedBrand.specialOption.regularCostForSameAmount) || originalCostToUse;
                        } else if (userChoice === 'minimal' && selectedBrand.specialOption?.minimalOption) {
                            costToUse = parseFloat(selectedBrand.specialOption.minimalOption.totalCost) || costToUse;
                            originalCostToUse = parseFloat(selectedBrand.specialOption.minimalOption.totalCost) || originalCostToUse;
                        }
                    }
                    
                    immediateTotalCost += costToUse;
                    immediateOriginalTotal += originalCostToUse;
                }
            }
        });

        // Calculate total savings correctly: originalTotal - finalTotal
        const immediateTotalSavings = immediateOriginalTotal - immediateTotalCost;

        // Update totals IMMEDIATELY
        setTotalCost(immediateTotalCost);
        setOriginalTotal(immediateOriginalTotal);
        setTotalSavings(immediateTotalSavings);
        
        console.log('⚡ IMMEDIATE totals update:', {
            totalCost: immediateTotalCost,
            originalTotal: immediateOriginalTotal,
            totalSavings: immediateTotalSavings,
            changedIngredient: ingredientName,
            newOption: option
        });
        
        // Find the selected brand to save the choice (do this after immediate update)
        const priceData = ingredientPrices[ingredientName];
        const selectedBrandId = selectedBrands[ingredientName];
        const selectedBrand = priceData?.availableBrands?.find(b => b.id === selectedBrandId);
        
        // Save user's choice to backend (non-blocking)
        if (selectedBrand) {
            saveOptionChoice(ingredientName, option, selectedBrand).catch(error => {
                console.error('Failed to save option choice:', error);
            });
        }
        
        // Optional: Trigger backend recalculation in background (non-blocking)
        setTimeout(async () => {
            try {
                const userId = window.localStorage.getItem('userID');
                console.log('🔄 Background backend recalculation...');
                
                const response = await axios.post(`${API_BASE_URL}/recipes/${recipeId}/calculate-cost`, {
                    ingredients: ingredients,
                    userId: userId,
                    preferredRegion: window.localStorage.getItem('preferredRegion') || null,
                    updateUserChoices: true
                });
                
                if (response.data.ingredientPrices) {
                    // Update ingredient prices but don't recalculate totals (keep user's immediate choice)
                    setIngredientPrices(response.data.ingredientPrices);
                    console.log('✅ Background backend update completed');
                    console.log('🔎 calculate-cost debug:', {
                        debugRegion: response.data?.debugRegion,
                        debugCollection: response.data?.debugCollection,
                        debugRegionSource: response.data?.debugRegionSource,
                        debugRequestPreferredRegion: response.data?.debugRequestPreferredRegion,
                        debugUserPreferredRegion: response.data?.debugUserPreferredRegion
                    });
                }
            } catch (error) {
                console.error('❌ Background backend update failed:', error);
            }
        }, 1000); // 1 second delay to not interfere with immediate user experience
    };

    const formatPriceDisplay = (brand, ingredient) => {
        if (!brand || !ingredient) return '';
        const { amount: normAmt, unit: normUnit } = getIngredientAmountUnit(ingredient);
        let priceDisplay = '';
        
        if (brand.baseUnit === 'R/kg') {
            // This is a per kg price item (like loose produce or meat)
            const amountNeeded = parseFloat(normAmt);
            const amountUnit = normUnit;
            const costPerKg = parseFloat(brand.costPerUnit);
            const cost = parseFloat(brand.cost);
            
            // Check if this is a special price for per-kg items
            if (brand.hasSpecial && brand.specialType === 'SPECIAL_PRICE') {
                const originalCostPerKg = parseFloat(brand.originalCost) / amountNeeded;
                const specialCostPerKg = parseFloat(brand.cost) / amountNeeded;
                priceDisplay = `R${cost.toFixed(2)} (${amountNeeded} ${amountUnit} @ R${specialCostPerKg.toFixed(2)}/kg, was R${originalCostPerKg.toFixed(2)}/kg) 🎉 SPECIAL`;
            } else {
            // Standard per kg display without any formatting issues
            priceDisplay = `R${cost.toFixed(2)} (${amountNeeded} ${amountUnit} @ R${costPerKg.toFixed(2)}/kg)`;
            }
            
            // Add any special information if applicable
            if (brand.special?.active) {
                switch (brand.special.type) {
                    case 'MULTI_BUY':
                        priceDisplay += ` - Special: Buy ${brand.special.buyQuantity} for R${parseFloat(brand.special.specialPrice).toFixed(2)}`;
                        break;
                    case 'PERCENTAGE_OFF':
                        const discountedPrice = costPerKg * (1 - brand.special.savingsPercentage / 100);
                        priceDisplay += ` - ${brand.special.savingsPercentage}% off (was R${costPerKg.toFixed(2)}/kg)`;
                        break;
                    case 'SPECIAL_PRICE':
                        const originalPrice = parseFloat(brand.originalCost);
                        const specialPrice = parseFloat(brand.special.specialPrice);
                        priceDisplay = `R${specialPrice.toFixed(2)} (was R${originalPrice.toFixed(2)})`;
                        if (brand.packageInfo) {
                            priceDisplay += ` (${brand.packageInfo})`;
                        } else if (brand.details) {
                            priceDisplay += ` (${brand.details})`;
                        }
                        break;
                    case 'FIXED_PRICE':
                        priceDisplay += ` - Special: R${parseFloat(brand.special.specialPrice).toFixed(2)}`;
                        break;
                    case 'BUY_X_GET_Y':
                        priceDisplay += ` - Buy ${brand.special.buyQuantity} Get ${brand.special.getQuantity} Free`;
                        break;
                }
            }
        } else {
            // This is a package-based item
            const cost = parseFloat(brand.cost);
            
                                    // For special price items, show both prices
            if (brand.hasSpecial && brand.specialType === 'SPECIAL_PRICE') {
                const originalPrice = parseFloat(brand.originalCost || brand.costPerUnit);
                const specialPrice = parseFloat(brand.cost);
                priceDisplay = `R${specialPrice.toFixed(2)} (was R${originalPrice.toFixed(2)}) 🎉 SPECIAL`;
                if (brand.packageInfo) {
                    priceDisplay += ` (${brand.packageInfo})`;
                }
            } else {
            // Default display: Final Cost (package info)
            priceDisplay = `R${cost.toFixed(2)}`;
            if (brand.packageInfo) {
                priceDisplay += ` (${brand.packageInfo})`;
                }
            }
            
            if (brand.special?.active) {
                switch (brand.special.type) {
                    case 'MULTI_BUY':
                        // Keep default display (final cost), but mention it's a multi-buy special
                        priceDisplay += ` (Multi-buy special applied)`; 
                        break;
                    case 'PERCENTAGE_OFF':
                        // Show final cost and % off
                        priceDisplay += ` (${brand.special.savingsPercentage}% off)`;
                        break;
                    case 'SPECIAL_PRICE':
                        // Show special price and original price
                        const originalPrice = parseFloat(brand.originalCost);
                        const specialPrice = parseFloat(brand.special.specialPrice);
                        priceDisplay = `R${specialPrice.toFixed(2)} (was R${originalPrice.toFixed(2)})`;
                        if (brand.packageInfo) {
                            priceDisplay += ` (${brand.packageInfo})`;
                        }
                        break;
                    // Keep FIXED_PRICE and BUY_X_GET_Y simple for now, showing final cost
                    case 'FIXED_PRICE':
                        priceDisplay += ` (Special price)`;
                        break;
                    case 'BUY_X_GET_Y':
                        priceDisplay += ` (Buy ${brand.special.buyQuantity} Get ${brand.special.getQuantity} Free applied)`;
                        break;
                }
            }
        }
        
        return priceDisplay;
    };

    const handleOrderIngredients = async () => {
        try {
            // Prepare shopping list items
            const shoppingListItems = [];
            
            ingredients.forEach(ingredient => {
                const priceData = ingredientPrices[ingredient.name];
                const selectedBrandId = selectedBrands[ingredient.name];
                
                if (selectedIngredients[ingredient.name] && selectedBrandId) {
                    const selectedBrand = priceData?.availableBrands?.find(
                        b => b.id === selectedBrandId
                    );

                    if (selectedBrand) {
                        // Normalize amount/unit for consolidated ingredients (e.g. MealPlanDetails uses amounts array)
                        const normAmount = ingredient.amount ?? ingredient.amounts?.[0]?.amount ?? 0;
                        const normUnit = ingredient.unit ?? ingredient.amounts?.[0]?.unit ?? '';

                        // Determine package size display
                        let packageSize = '';
                        if (selectedBrand.packageInfo) {
                            packageSize = selectedBrand.packageInfo;
                        } else if (selectedBrand.baseUnit === 'R/kg') {
                            packageSize = `${normAmount}${normUnit}`;
                        } else {
                            packageSize = `${normAmount}${normUnit}`;
                        }
                        
                        // Calculate packages needed first for all items
                        let packagesNeeded = 1;
                        
                        // Calculate packages needed based on ingredient amount vs package size
                        const isKgBased = selectedBrand.baseUnit && (
                            selectedBrand.baseUnit.toLowerCase().includes('per kg') ||
                            selectedBrand.baseUnit.toLowerCase().includes('r/kg') ||
                            selectedBrand.baseUnit.toLowerCase() === 'kg'
                        );
                        
                        if (!isKgBased) {
                            let packageSizeValue = 0;
                            let packageSizeUnit = 'g';
                            
                            // Get package size first
                            if (selectedBrand.unitEquivalent) {
                                packageSizeValue = parseFloat(selectedBrand.unitEquivalent.amount) || 0;
                                packageSizeUnit = selectedBrand.unitEquivalent.unit || 'g';
                            } else if (selectedBrand.packageInfo) {
                                const match = selectedBrand.packageInfo.match(/(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
                                if (match) {
                                    packageSizeValue = parseFloat(match[1]);
                                    packageSizeUnit = match[2];
                                }
                            }

                            // Calculate packages needed based on ingredient amount vs package size
                            if (packageSizeValue > 0) {
                                const ingredientAmount = parseFloat(normAmount) || 0;
                                let ingredientAmountInGrams = ingredientAmount;
                                let packageSizeInGrams = packageSizeValue;
                                
                                // Convert ingredient amount to grams for comparison
                                const ingredientUnit = (normUnit || '').toLowerCase();
                                if (ingredientUnit === 'kg') {
                                    ingredientAmountInGrams = ingredientAmount * 1000;
                                } else if (ingredientUnit === 'tsp' || ingredientUnit === 'teaspoon') {
                                    ingredientAmountInGrams = ingredientAmount * 5; // 1 tsp = 5g for dry ingredients like spices
                                } else if (ingredientUnit === 'tbsp' || ingredientUnit === 'tablespoon') {
                                    ingredientAmountInGrams = ingredientAmount * 15; // 1 tbsp = 15g for dry ingredients
                                } else if (ingredientUnit === 'cup' || ingredientUnit === 'cups') {
                                    ingredientAmountInGrams = ingredientAmount * 250; // 1 cup = 250g for dry ingredients
                                } else if (ingredientUnit === 'ml') {
                                    ingredientAmountInGrams = ingredientAmount; // 1ml ≈ 1g for most liquids
                                } else if (ingredientUnit === 'l') {
                                    ingredientAmountInGrams = ingredientAmount * 1000; // 1L = 1000g
                                }
                                // If unit is already 'g', no conversion needed
                                
                                // Convert package size to grams/ml for comparison
                                if (packageSizeUnit.toLowerCase() === 'kg') {
                                    packageSizeInGrams = packageSizeValue * 1000;
                                } else if (packageSizeUnit.toLowerCase() === 'l') {
                                    packageSizeInGrams = packageSizeValue * 1000; // 1L = 1000ml
                                } else if (packageSizeUnit.toLowerCase() === 'ml') {
                                    packageSizeInGrams = packageSizeValue; // ml stays as ml for liquid comparison
                                }
                                
                                // Calculate actual packages needed
                                packagesNeeded = Math.ceil(ingredientAmountInGrams / packageSizeInGrams);
                            }
                        }
                        
                        // Determine the correct cost and quantity based on user's choice
                        let finalCost = selectedBrand.cost || 0;
                        let finalQuantity = 1; // Default to 1 - will be adjusted based on item type
                        let finalSavings = selectedBrand.savings || 0;
                        let finalOriginalCost = selectedBrand.originalCost || finalCost;
                        let specialInfo = null;
                        let finalExcess = selectedBrand.excess || 0;
                        let costPerUnit = parseFloat(selectedBrand.costPerUnit) || parseFloat(selectedBrand.cost) || 0; // Cost per single package

                        // Check if user chose special option for items with specials
                        const userChoiceIsSpecial = displaySpecialOption[ingredient.name] === 'special';
                        
                        if (selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY') {
                            if (userChoiceIsSpecial && selectedBrand.specialOption) {
                                // User chose the special deal - use special option data
                                // For multi-buy specials, quantity should be 1 (representing one deal)
                                // The totalCost already includes the cost for all packages in the deal
                                finalCost = parseFloat(selectedBrand.specialOption.totalCost);
                                finalOriginalCost = parseFloat(selectedBrand.specialOption.regularCostForSameAmount);
                                finalSavings = parseFloat(selectedBrand.specialOption.savings);
                                finalQuantity = 1; // Always 1 for multi-buy deals (one deal unit)
                                finalExcess = selectedBrand.specialOption.excess || 0;
                                
                                specialInfo = {
                                    type: 'MULTI_BUY',
                                    buyQuantity: selectedBrand.specialOption.buyQuantity,
                                    specialPrice: selectedBrand.specialOption.specialPrice,
                                    savings: selectedBrand.specialOption.savings,
                                    specialGroupsNeeded: selectedBrand.specialOption.specialGroupsNeeded,
                                    totalPackagesPurchased: selectedBrand.specialOption.totalPackagesPurchased,
                                    totalCost: selectedBrand.specialOption.totalCost
                                };
                            } else if (selectedBrand.specialOption?.minimalOption) {
                                // User chose minimal option - use minimal option data
                                finalCost = parseFloat(selectedBrand.specialOption.minimalOption.totalCost);
                                finalOriginalCost = parseFloat(selectedBrand.specialOption.minimalOption.totalCost); // No savings for minimal
                                finalSavings = 0;
                                finalQuantity = 1; // Always 1 - the totalCost already includes cost for needed packages
                                finalExcess = 0; // No excess for minimal purchase
                                
                                specialInfo = {
                                    type: 'MINIMAL_BUY',
                                    packagesNeeded: 1,
                                    shortfall: selectedBrand.specialOption.minimalOption.shortfall,
                                    warningMessage: selectedBrand.specialOption.minimalOption.warningMessage
                                }; // Mark as minimal choice
                            } else if (selectedBrand.regularOption?.minimalOption) {
                                // Fallback to regular option's minimal if available
                                finalCost = parseFloat(selectedBrand.regularOption.minimalOption.totalCost);
                                finalOriginalCost = parseFloat(selectedBrand.regularOption.minimalOption.totalCost); // No savings for minimal
                                finalSavings = 0;
                                finalQuantity = 1; // Always 1 - the totalCost already includes cost for needed packages
                                finalExcess = 0; // No excess for minimal purchase
                                
                                specialInfo = {
                                    type: 'MINIMAL_BUY',
                                    packagesNeeded: 1,
                                    shortfall: selectedBrand.regularOption.minimalOption.shortfall,
                                    warningMessage: selectedBrand.regularOption.minimalOption.warningMessage
                                }; // Mark as minimal choice
                            }
                        } else if (selectedBrand.hasSpecial && selectedBrand.specialType === 'SPECIAL_PRICE') {
                            // Special price items - automatically applied (no user choice)
                            // Keep the calculated packagesNeeded quantity since these are just discounted regular items
                            finalCost = parseFloat(selectedBrand.cost);
                            finalOriginalCost = parseFloat(selectedBrand.originalCost || selectedBrand.cost);
                            finalSavings = parseFloat(selectedBrand.savings || 0);
                            // finalQuantity already set to packagesNeeded above - don't override it
                            
                            specialInfo = {
                                type: 'SPECIAL_PRICE',
                                specialPrice: selectedBrand.cost,
                                originalPrice: selectedBrand.originalCost,
                                savings: selectedBrand.savings
                            };
                        } else {
                            // For non-special items, use calculated packages needed
                            // IMPORTANT: For regular items, finalCost should be PER PACKAGE, not total
                            // The shopping list will multiply cost × quantity
                            finalQuantity = packagesNeeded;
                            
                            // Calculate per-package cost for regular items
                            if (packagesNeeded > 1 && selectedBrand.cost) {
                                // If cost is total for all packages, divide by packages needed to get per-package cost
                                finalCost = parseFloat(selectedBrand.cost) / packagesNeeded;
                                finalOriginalCost = parseFloat(selectedBrand.originalCost || selectedBrand.cost) / packagesNeeded;
                                finalSavings = parseFloat(selectedBrand.savings || 0) / packagesNeeded;
                            } else {
                                // Single package, use the cost as-is
                                finalCost = parseFloat(selectedBrand.cost);
                                finalOriginalCost = parseFloat(selectedBrand.originalCost || selectedBrand.cost);
                                finalSavings = parseFloat(selectedBrand.savings || 0);
                            }
                            
                            console.log(`📦 Regular item ${ingredient.name}: quantity=${finalQuantity}, costPerPackage=${finalCost.toFixed(2)}, total=${(finalCost * finalQuantity).toFixed(2)}`);
                        }
                        
                        // FINAL SAFETY CHECK: Force quantity to 1 if user selected special
                        if (userChoiceIsSpecial) {
                            console.log(`🔒 FINAL CHECK: User chose special for ${ingredient.name}, forcing quantity to 1 (was ${finalQuantity})`);
                            finalQuantity = 1;
                            // For specials, cost is already the total, don't divide
                            finalCost = parseFloat(selectedBrand.cost);
                            finalOriginalCost = parseFloat(selectedBrand.originalCost || selectedBrand.cost);
                        }
                        
                        // For non-special items or per kg items, use the default values
                        
                        // Calculate excess amounts for cupboard tracking based on user's choice
                        let excessAmount = 0;
                        let excessUnit = normUnit; // default to recipe unit – will adjust later
                        let totalAmountPurchased = 0;
                        let totalAmountPurchasedUnit = normUnit;
                        
                        // For multi-buy specials, packagesNeeded should be the actual packages purchased
                        // even though finalQuantity is 1 (representing the deal itself)
                        if (selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY' && userChoiceIsSpecial && selectedBrand.specialOption) {
                            packagesNeeded = selectedBrand.specialOption.totalPackagesPurchased || packagesNeeded;
                        }
                        
                        // Use the finalExcess calculated based on user's choice
                        if (finalExcess > 0) {
                            // Convert excess from kg to ingredient unit
                            const excessInKg = finalExcess;
                            const ingUnit = (normUnit || '').toLowerCase();
                            if (ingUnit === 'kg') {
                                excessAmount = excessInKg;
                                excessUnit = 'kg';
                            } else if (ingUnit === 'g') {
                                excessAmount = excessInKg * 1000;
                                excessUnit = 'g';
                            } else if (ingUnit === 'l') {
                                excessAmount = excessInKg; // assume 1 kg ≈ 1 l (water-based) as fallback
                                excessUnit = 'l';
                            } else if (ingUnit === 'ml') {
                                excessAmount = excessInKg * 1000;
                                excessUnit = 'ml';
                            } else {
                                excessAmount = excessInKg;
                                excessUnit = 'kg';
                            }
                        }
                        
                        // Calculate total amount purchased based on type of pricing
                        if (selectedBrand.baseUnit === 'R/kg') {
                            // For per kg items, total purchased = amount needed (no excess in this case)
                            totalAmountPurchased = parseFloat(normAmount);
                            totalAmountPurchasedUnit = normUnit;
                            excessAmount = 0; // No excess for exact weight purchases
                        } else {
                            // For package-based items, use backend data if available to override our calculation
                            if (selectedBrand.hasSpecial && selectedBrand.specialType === 'SPECIAL_PRICE' && selectedBrand.specialOption) {
                                packagesNeeded = selectedBrand.specialOption.packagesNeeded || packagesNeeded;
                            } else if (selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY') {
                                if (userChoiceIsSpecial && selectedBrand.specialOption) {
                                    packagesNeeded = selectedBrand.specialOption.totalPackagesPurchased || packagesNeeded;
                                } else if (selectedBrand.specialOption?.minimalOption) {
                                    packagesNeeded = selectedBrand.specialOption.minimalOption.packagesNeeded || packagesNeeded;
                                } else {
                                    packagesNeeded = selectedBrand.regularOption?.packagesNeeded || packagesNeeded;
                                }
                            } else if (selectedBrand.regularOption) {
                                packagesNeeded = selectedBrand.regularOption.packagesNeeded || packagesNeeded;
                            } else {
                                // Fallback to parsing details string only if no backend data
                                if (selectedBrand.details && selectedBrand.details.includes('×')) {
                                    const parsedPackages = parseInt(selectedBrand.details.split('×')[0].trim()) || 1;
                                    packagesNeeded = parsedPackages;
                                }
                            }
                            
                            // Try to extract package size from various sources
                            let packageSizeValue = 0;
                            let packageSizeUnit = 'g';
                            
                            if (selectedBrand.unitEquivalent) {
                                packageSizeValue = parseFloat(selectedBrand.unitEquivalent.amount) || 0;
                                packageSizeUnit = selectedBrand.unitEquivalent.unit || 'g';
                            } else if (selectedBrand.packageInfo) {
                                // Try to extract from packageInfo like "500g" or "1.5kg"
                                const match = selectedBrand.packageInfo.match(/(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
                                if (match) {
                                    packageSizeValue = parseFloat(match[1]);
                                    packageSizeUnit = match[2];
                                }
                            }
                            
                            if (packageSizeValue > 0) {
                                totalAmountPurchased = packagesNeeded * packageSizeValue;
                                totalAmountPurchasedUnit = packageSizeUnit;
                                excessUnit = packageSizeUnit; // leftover should be in the same unit as package

                                // Calculate excess in appropriate units
                                // Convert recipe amount to package units for comparison
                                let neededInPackageUnits = parseFloat(normAmount);
                                
                                if ((normUnit || '').toLowerCase() !== packageSizeUnit.toLowerCase()) {
                                    // Convert both to common base units for calculation
                                    let neededInBaseUnits = neededInPackageUnits;
                                    let purchasedInBaseUnits = totalAmountPurchased;
                                    
                                    // Helper function to convert to base units (grams for mass, ml for volume)
                                    const convertToBaseUnits = (amount, unit) => {
                                        const unitLower = (unit || '').toLowerCase();
                                        // Mass conversions (to grams)
                                        if (unitLower === 'kg') return amount * 1000;
                                        if (unitLower === 'g') return amount;
                                        // Volume conversions (to ml)
                                        if (unitLower === 'l') return amount * 1000;
                                        if (unitLower === 'ml') return amount;
                                        // Kitchen unit conversions
                                        if (unitLower === 'tsp' || unitLower === 'teaspoon') return amount * 5;
                                        if (unitLower === 'tbsp' || unitLower === 'tablespoon') return amount * 15;
                                        if (unitLower === 'cup' || unitLower === 'cups') return amount * 250;
                                        // Default: return as-is
                                        return amount;
                                    };
                                    
                                    // Check if we're dealing with volume or mass units
                                    const isVolumeUnit = (unit) => {
                                        const unitLower = (unit || '').toLowerCase();
                                        return ['ml', 'l', 'tsp', 'teaspoon', 'tbsp', 'tablespoon', 'cup', 'cups'].includes(unitLower);
                                    };
                                    
                                    const recipeIsVolume = isVolumeUnit(normUnit);
                                    const packageIsVolume = isVolumeUnit(packageSizeUnit);
                                    
                                    // Only convert if both are the same type (both volume or both mass)
                                    if (recipeIsVolume === packageIsVolume) {
                                        neededInBaseUnits = convertToBaseUnits(neededInPackageUnits, normUnit);
                                        purchasedInBaseUnits = convertToBaseUnits(totalAmountPurchased, packageSizeUnit);
                                        
                                        const excessInBaseUnits = Math.max(0, purchasedInBaseUnits - neededInBaseUnits);
                                        
                                        // Convert excess back to appropriate display unit
                                        if (packageIsVolume) {
                                            // Volume units: prefer package unit or convert to L if large amounts
                                            if (packageSizeUnit.toLowerCase() === 'l') {
                                                excessAmount = excessInBaseUnits / 1000; // ml to L
                                                excessUnit = 'L';
                                            } else if (excessInBaseUnits >= 1000) {
                                                excessAmount = excessInBaseUnits / 1000; // ml to L
                                                excessUnit = 'L';
                                            } else {
                                                excessAmount = excessInBaseUnits; // keep as ml
                                                excessUnit = 'ml';
                                            }
                                        } else {
                                            // Mass units: prefer package unit or convert to kg if large amounts
                                            if (packageSizeUnit.toLowerCase() === 'kg') {
                                                excessAmount = excessInBaseUnits / 1000; // g to kg
                                                excessUnit = 'kg';
                                            } else if (excessInBaseUnits >= 1000) {
                                                excessAmount = excessInBaseUnits / 1000; // g to kg
                                                excessUnit = 'kg';
                                            } else {
                                                excessAmount = excessInBaseUnits; // keep as g
                                                excessUnit = 'g';
                                            }
                                        }
                                    } else {
                                        // Different unit types (volume vs mass) - can't calculate excess
                                        excessAmount = 0;
                                        excessUnit = packageSizeUnit;
                                    }
                                } else {
                                    // Same units, direct calculation
                                    excessAmount = Math.max(0, totalAmountPurchased - neededInPackageUnits);
                                    excessUnit = packageSizeUnit;
                                }
                            }
                        }
                        
                        const shoppingItem = {
                            name: ingredient.name,
                            amount: typeof normAmount === 'number' ? normAmount : parseFloat(normAmount) || 0,
                            unit: (normUnit && String(normUnit)) || 'unit',
                            quantity: finalQuantity,
                            packageSize: packageSize,
                            brand: selectedBrand.brand || '',
                            brandName: selectedBrand.name || '',
                            brandId: selectedBrand.id,
                            cost: finalCost,
                            originalCost: finalOriginalCost,
                            savings: finalSavings,
                            imageUrl: selectedBrand.imageUrl || '',
                            originalRecipeId: window.location.pathname.split('/').pop(), // Extract recipe ID from URL
                            dateAdded: new Date().toISOString(),
                            specialInfo: specialInfo, // Include special offer details
                            userSelectedSpecial: userChoiceIsSpecial,
                            userChoice: userChoiceIsSpecial ? 'special' : 'minimal', // Explicit choice for backend
                            // Include both options so backend knows what the alternatives were
                            regularOption: selectedBrand.regularOption || null,
                            specialOption: selectedBrand.specialOption || null,
                            // Excess tracking information
                            excessAmount: excessAmount,
                            excessUnit: excessUnit,
                            totalAmountPurchased: totalAmountPurchased,
                            totalAmountPurchasedUnit: totalAmountPurchasedUnit,
                            packagesNeeded: packagesNeeded
                        };
                        
                        // CRITICAL DEBUG: Log what we're actually sending
                        if (ingredient.name.toLowerCase().includes('kidney')) {
                            console.log(`🚨 KIDNEY BEANS DEBUG:`, {
                                ingredientName: ingredient.name,
                                hasSpecial: selectedBrand.hasSpecial,
                                specialType: selectedBrand.specialType,
                                userChoiceIsSpecial,
                                packagesNeeded,
                                finalQuantity,
                                finalCost,
                                specialInfo,
                                selectedBrand: {
                                    cost: selectedBrand.cost,
                                    specialOption: selectedBrand.specialOption
                                }
                            });
                        }
                        
                        shoppingListItems.push(shoppingItem);
                    }
                }
            });

            if (shoppingListItems.length === 0) {
                return;
            }

            if (!tokenValidated || !getAuthToken()) {
                if (onRequireSignIn && typeof onRequireSignIn === 'function') {
                    onRequireSignIn();
                } else {
                    alert('Please log in to add ingredients to your shopping list.');
                }
                return;
            }

            // Send items to backend
            const response = await axios.post(
                `${API_BASE_URL}/shopping-list/add`, 
                { items: shoppingListItems },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    }
                }
            );
            
            if (response.data.success) {
                // Navigate to the shopping list page
                navigate('/shopping-list');
            } else {
                // Handle error silently
            }
        } catch (error) {
            console.error('Error adding to shopping list:', error);
            // Handle errors silently
        }
    };

    return (
        <div className="modern-recipe-costs" data-recipe-price-details>
            {cupboardIngredients.length > 0 && (
                <div className="cupboard-notice-modern">
                    <div className="notice-content">
                        <svg className="notice-icon" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <p>Ingredients you have in your cupboard are automatically unchecked and excluded from the total cost.</p>
                    </div>
                </div>
            )}
            
            {/* Show preferences notice if user has saved preferences being used */}
            {(() => {
                const preferencesUsed = ingredients.filter(ingredient => {
                    const priceData = ingredientPrices[ingredient.name];
                    return priceData?.selectedBrand?.preferenceMatch || priceData?.selectedBrand?.isPreferred;
                });
                
                // return preferencesUsed.length > 0 && (
                //     // <div className="preferences-notice">
                //     //     <p>
                //     //         <span className="notice-icon">⭐</span>
                //     //         Using your saved preferences for {preferencesUsed.length} ingredient{preferencesUsed.length > 1 ? 's' : ''}: {preferencesUsed.map(ing => ing.name).join(', ')}
                //     //     </p>
                //     // </div>
                // );
            })()}
            
            {/* Modern Ingredients Grid */}
            <div className="ingredients-grid-modern">
                {ingredients
                    .filter(ingredient => selectedIngredients[ingredient.name]) // Only show selected ingredients
                    .map((ingredient, index) => {
                    console.log(`Frontend rendering ingredient: ${ingredient.name}`);
                    const priceData = ingredientPrices[ingredient.name];
                    console.log(`Price data for ${ingredient.name}:`, priceData);
                    console.log(`selectedIngredients[${ingredient.name}]:`, selectedIngredients[ingredient.name]);
                    console.log(`selectedBrands[${ingredient.name}]:`, selectedBrands[ingredient.name]);
                    
                    if (!priceData) {
                        console.log(`❌ No price data for ${ingredient.name}, skipping render`);
                        return null;
                    }

                    const selectedBrand = priceData.availableBrands.find(
                        b => b.id === selectedBrands[ingredient.name]
                    );

                    // No match = no selected brand and no available brands (exact-match only from backend)
                    const isUnmatched = !priceData.selectedBrand && (!priceData.availableBrands || priceData.availableBrands.length === 0);

                    // Check if ingredient is in cupboard
                    const inCupboard = cupboardIngredients.some(cupboardItem => 
                        cupboardItem.ingredientName.toLowerCase() === ingredient.name.toLowerCase()
                    );

                    const { amount: displayAmount, unit: displayUnit } = getIngredientAmountUnit(ingredient);

                    // Get the actual index of visible ingredients (after filtering)
                    const visibleIngredients = ingredients.filter(ing => selectedIngredients[ing.name]);
                    const isFirstVisible = visibleIngredients[0]?.name === ingredient.name;

                    return (
                        <div 
                            key={`${ingredient.name}-${index}`} 
                            className={`ingredient-card-modern mobile-ingredient-card ${inCupboard ? 'in-cupboard' : ''}${openDropdownIngredientName === ingredient.name ? ' ingredient-card-dropdown-open' : ''}`}
                            onClick={(e) => handleIngredientItemClick(e, ingredient.name)}
                            data-ingredient-card={isFirstVisible ? 'first' : undefined}
                            data-ingredient-name={ingredient.name}
                        >
                            <div className="mobile-ingredient-header">
                                <div className="mobile-ingredient-info">
                                    <div className="add-to-cupboard-row">
                                        <button
                                            type="button"
                                            className={`add-to-cupboard-btn ${inCupboard ? 'added' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddToCupboard(ingredient);
                                            }}
                                            disabled={isAddingToCupboard || inCupboard}
                                            title={inCupboard ? 'Already in cupboard' : 'Add to cupboard and exclude from list'}
                                            aria-label="Add to cupboard"
                                        >
                                            <svg className="cupboard-plus-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M4 12h16" />
                                            </svg>
                                        </button>
                                        <span className="add-to-cupboard-label">Cupboard</span>
                                    </div>
                                    <div className="mobile-ingredient-details">
                                        <h4 className={`mobile-ingredient-name ${isUnmatched ? 'ingredient-unmatched' : ''}`}>{ingredient.name}</h4>
                                        <p className="mobile-ingredient-amount">{formatIngredientAmount(displayAmount, displayUnit)}</p>
                                    </div>
                                    {inCupboard && (
                                        <span className="mobile-cupboard-badge" title="You have this in your cupboard">
                                            🏠
                                        </span>
                                    )}
                                </div>
                            </div>

                            {priceData.availableBrands.length > 0 ? (
                                <div className="brand-selection-modern" onClick={(e) => e.stopPropagation()}>
                                    {/* Integrated Brand Preview with Dropdown */}
                                    <CustomDropdown
                                        ingredient={ingredient}
                                        priceData={priceData}
                                        selectedBrands={selectedBrands}
                                        expandedDropdowns={expandedDropdowns}
                                        isLoadingExpansion={isLoadingExpansion}
                                        getBrandsToDisplay={getBrandsToDisplay}
                                        handleBrandChange={handleBrandChange}
                                        displayAmount={displayAmount}
                                        displayUnit={displayUnit}
                                        displaySpecialOption={displaySpecialOption}
                                        onOpenChange={(open) => {
                                            setOpenDropdownIngredientName(open ? ingredient.name : null);
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="no-brands-modern">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <span>No brands available</span>
                                </div>
                            )}

                            {/* Mobile: Show price and special info below dropdown */}
                            {selectedBrand && (
                                <div className="mobile-brand-details" onClick={(e) => e.stopPropagation()}>
                                    {/* Mobile Special Info */}
                                    {selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY' && selectedBrand.specialOfferInfo && (
                                        <div className="mobile-special-deal-info">
                                            <span className="mobile-special-deal-text">
                                                Buy {selectedBrand.specialOfferInfo.details?.totalPackagesPurchased || selectedBrand.special?.buyQuantity || ''} for R{parseFloat(selectedBrand.cost).toFixed(2)}
                                            </span>
                                            {selectedBrand.savings > 0 && (
                                                <span className="mobile-savings-badge">
                                                    Save R{parseFloat(selectedBrand.savings).toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Ingredient Link Button - Visible on Mobile */}
                                    {selectedBrand.ingredient_link && (
                                        <div className="ingredient-link-container mobile-visible">
                                            <a
                                                href={selectedBrand.ingredient_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="ingredient-link-button"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                                <span>View Product</span>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {selectedBrand && (
                                <div className="brand-details-modern mobile-hidden" onClick={(e) => e.stopPropagation()}>
                                    <div className="details-grid">
                                        <div className="detail-item-modern">
                                            <span className="detail-label-modern">Price:</span>
                                            <div className="price-display-modern">
                                                {/* Show strikethrough original price for special price items */}
                                                {selectedBrand.specialType === 'SPECIAL_PRICE' && selectedBrand.originalCost && (
                                                    <span className="original-price-modern">
                                                        R{parseFloat(selectedBrand.originalCost).toFixed(2)}
                                                    </span>
                                                )}
                                                <span className="current-price-modern">R{parseFloat(selectedBrand.cost).toFixed(2)}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="detail-item-modern">
                                            <span className="detail-label-modern">Amount Needed:</span>
                                            <span className="detail-value-modern">{formatIngredientAmount(displayAmount, displayUnit)}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Ingredient Link Button */}
                                    {selectedBrand.ingredient_link && (
                                        <div className="ingredient-link-container">
                                            <a
                                                href={selectedBrand.ingredient_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="ingredient-link-button"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                                <span>View Product</span>
                                            </a>
                                        </div>
                                    )}

                                    {/* Containers needed information */}
                                    {(() => {
                                        const isKgBased = selectedBrand.baseUnit && (
                                            selectedBrand.baseUnit.toLowerCase().includes('per kg') ||
                                            selectedBrand.baseUnit.toLowerCase().includes('r/kg') ||
                                            selectedBrand.baseUnit.toLowerCase() === 'kg'
                                        );
                                        
                                        if (isKgBased) {
                                            return null; // No container info for per kg purchases
                                        }

                                        let packageSizeValue = 0;
                                        let packageSizeUnit = 'g';
                                        
                                        // Get package size first
                                        if (selectedBrand.unitEquivalent) {
                                            packageSizeValue = parseFloat(selectedBrand.unitEquivalent.amount) || 0;
                                            packageSizeUnit = selectedBrand.unitEquivalent.unit || 'g';
                                        } else if (selectedBrand.packageInfo) {
                                            const match = selectedBrand.packageInfo.match(/(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
                                            if (match) {
                                                packageSizeValue = parseFloat(match[1]);
                                                packageSizeUnit = match[2];
                                            }
                                        }

                                        // Calculate packages needed based on ingredient amount vs package size
                                        let packagesNeeded = 1;
                                        if (packageSizeValue > 0) {
                                            const ingredientAmount = parseFloat(displayAmount) || 0;
                                            let ingredientAmountInGrams = ingredientAmount;
                                            let packageSizeInGrams = packageSizeValue;
                                            
                                            // Convert ingredient amount to grams for comparison
                                            const ingredientUnit = (displayUnit || '').toLowerCase();
                                            if (ingredientUnit === 'kg') {
                                                ingredientAmountInGrams = ingredientAmount * 1000;
                                            } else if (ingredientUnit === 'tsp' || ingredientUnit === 'teaspoon') {
                                                ingredientAmountInGrams = ingredientAmount * 5; // 1 tsp = 5g for dry ingredients like spices
                                            } else if (ingredientUnit === 'tbsp' || ingredientUnit === 'tablespoon') {
                                                ingredientAmountInGrams = ingredientAmount * 15; // 1 tbsp = 15g for dry ingredients
                                            } else if (ingredientUnit === 'cup' || ingredientUnit === 'cups') {
                                                ingredientAmountInGrams = ingredientAmount * 250; // 1 cup = 250g for dry ingredients
                                            } else if (ingredientUnit === 'ml') {
                                                ingredientAmountInGrams = ingredientAmount; // 1ml ≈ 1g for most liquids
                                            } else if (ingredientUnit === 'l') {
                                                ingredientAmountInGrams = ingredientAmount * 1000; // 1L = 1000g
                                            }
                                            // If unit is already 'g', no conversion needed
                                            
                                            // Convert package size to grams/ml for comparison
                                            if (packageSizeUnit.toLowerCase() === 'kg') {
                                                packageSizeInGrams = packageSizeValue * 1000;
                                            } else if (packageSizeUnit.toLowerCase() === 'l') {
                                                packageSizeInGrams = packageSizeValue * 1000; // 1L = 1000ml
                                            } else if (packageSizeUnit.toLowerCase() === 'ml') {
                                                packageSizeInGrams = packageSizeValue; // ml stays as ml for liquid comparison
                                            }
                                            
                                            // Calculate actual packages needed
                                            packagesNeeded = Math.ceil(ingredientAmountInGrams / packageSizeInGrams);
                                        }

                                        const userChoiceIsSpecial = displaySpecialOption[ingredient.name] === 'special';
                                        
                                        // For special offers, adjust packages needed based on user choice
                                        if (selectedBrand.hasSpecial && selectedBrand.specialType === 'MULTI_BUY') {
                                            if (userChoiceIsSpecial && selectedBrand.specialOption) {
                                                packagesNeeded = selectedBrand.specialOption.totalPackagesPurchased || packagesNeeded;
                                            } else if (selectedBrand.specialOption?.minimalOption) {
                                                packagesNeeded = selectedBrand.specialOption.minimalOption.packagesNeeded || 1;
                                            }
                                        }

                                        if (packageSizeValue > 0) {
                                            return (
                                                <div className="container-volume-info">
                                                    <span className="detail-label">
                                                        {packagesNeeded > 1 ? 'Containers Needed:' : 'Package Size:'}
                                                    </span>
                                                    <span className="detail-value">
                                                        {packagesNeeded > 1 ? 
                                                            `${packagesNeeded} × ${packageSizeValue}${packageSizeUnit}` : 
                                                            `${packageSizeValue}${packageSizeUnit}`
                                                        }
                                                    </span>
                                                </div>
                                            );
                                        }

                                        return null;
                                    })()}

                                    {/* Use backend-calculated excess values - HIDDEN ON MOBILE */}
                                    <div className="mobile-hidden">
                                        {(() => {
                                            // Check if the brand has excess (leftover) amount calculated by backend
                                            if (selectedBrand && selectedBrand.excess > 0) {
                                                const excessAmount = selectedBrand.excess;
                                                const excessUnit = selectedBrand.excessUnit || 'g';
                                                
                                                // Format the excess display with proper unit conversions
                                                let excessDisplay;
                                                
                                                if (excessUnit.toLowerCase() === 'g') {
                                                    // Show in kg if >= 1000g, otherwise show in g
                                                    if (excessAmount >= 1000) {
                                                        excessDisplay = `${(excessAmount / 1000).toFixed(1)}kg`;
                                                    } else {
                                                        excessDisplay = `${Math.round(excessAmount)}g`;
                                                    }
                                                } else if (excessUnit.toLowerCase() === 'ml') {
                                                    // Show in L if >= 1000ml, otherwise show in ml
                                                    if (excessAmount >= 1000) {
                                                        excessDisplay = `${(excessAmount / 1000).toFixed(1)}L`;
                                                    } else {
                                                        excessDisplay = `${Math.round(excessAmount)}ml`;
                                                    }
                                                } else {
                                                    // Use the unit as provided by backend (kg, L, etc.)
                                                    excessDisplay = `${excessAmount.toFixed(1)}${excessUnit}`;
                                                }
                                                
                                                return (
                                                    <div className="excess-amount-info">
                                                        <span className="detail-label">Leftover:</span>
                                                        <span className="detail-value excess-highlight">
                                                            {excessDisplay}
                                                        </span>
                                                    </div>
                                                );
                                            }
                                            
                                            return null;
                                        })()}
                                    </div>
                                </div>
                            )}
                            
                            {selectedBrand && selectedBrand.hasSpecial && (
                                <div className="special-offer mobile-hidden" onClick={(e) => e.stopPropagation()}>
                                    {/* Enhanced Special Price Badge */}
                                    {selectedBrand.specialType === 'SPECIAL_PRICE' && (
                                        <div className="compact-special-badge special-price">
                                            <span className="special-price-text">SPECIAL PRICE: R{selectedBrand.cost.toFixed(2)}</span>
                                            <span className="savings-percentage">
                                                {(() => {
                                                    const original = parseFloat(selectedBrand.originalCost || selectedBrand.costPerUnit);
                                                    const special = parseFloat(selectedBrand.cost);
                                                    const savingsPercent = ((original - special) / original * 100).toFixed(0);
                                                    return savingsPercent > 0 ? `${savingsPercent}% OFF` : '';
                                                })()}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Compact Multi-Buy Badge with simplified options */}
                                    {selectedBrand.specialOfferInfo?.type === 'MULTI_BUY' && (
                                        <>
                                            <div className="compact-special-badge multi-buy">
                                                <span className="multi-buy-text">
                                                    BUY {selectedBrand.specialOfferInfo.details.totalPackagesPurchased} FOR R{selectedBrand.cost.toFixed(2)}
                                                </span>
                                                <span className="savings-percentage">
                                                    {(() => {
                                                        const savings = parseFloat(selectedBrand.savings || 0);
                                                        const original = parseFloat(selectedBrand.originalCost || 0);
                                                        if (savings > 0 && original > 0) {
                                                            const savingsPercent = (savings / original * 100).toFixed(0);
                                                            return `${savingsPercent}% OFF`;
                                                        }
                                                        return '';
                                                    })()}
                                                </span>
                                            </div>
                                            
                                            {/* Simple option selector */}
                                            <div className="simple-option-selector">
                                                <label className="option-choice">
                                                    <input 
                                                        type="radio" 
                                                        name={`pricing-${ingredient.name}`}
                                                        value="minimal"
                                                        checked={displaySpecialOption[ingredient.name] !== 'special'}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleDisplayOptionChange(ingredient.name, 'minimal');
                                                        }}
                                                    />
                                                    <span className="option-text">
                                                        Buy 1 for R{selectedBrand.specialOption?.minimalOption?.totalCost?.toFixed(2) || selectedBrand.regularOption?.minimalOption?.totalCost?.toFixed(2) || '0.00'}
                                                    </span>
                                                </label>
                                                
                                                <label className="option-choice special">
                                                    <input 
                                                        type="radio" 
                                                        name={`pricing-${ingredient.name}`}
                                                        value="special"
                                                        checked={displaySpecialOption[ingredient.name] === 'special'}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleDisplayOptionChange(ingredient.name, 'special');
                                                        }}
                                                    />
                                                    <span className="option-text">
                                                        Take deal: R{selectedBrand.cost.toFixed(2)}
                                                    </span>
                                                </label>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Deselected ingredients section - compact one-liner at the bottom */}
            {(() => {
                const deselectedIngredients = ingredients.filter(ingredient => !selectedIngredients[ingredient.name]);
                
                if (deselectedIngredients.length === 0) {
                    return null;
                }

                return (
                    <div className="deselected-ingredients-section">
                        <h4 className="deselected-title">Not needed (click to add back):</h4>
                        <div className="deselected-ingredients-list">
                            {deselectedIngredients.map((ingredient, index) => {
                                // Check if ingredient is in cupboard
                                const inCupboard = cupboardIngredients.some(cupboardItem => 
                                    cupboardItem.ingredientName.toLowerCase() === ingredient.name.toLowerCase()
                                );
                                const priceData = ingredientPrices[ingredient.name];
                                const isUnmatched = priceData && !priceData.selectedBrand && (!priceData.availableBrands || priceData.availableBrands.length === 0);
                                const { amount: dispAmt, unit: dispUnit } = getIngredientAmountUnit(ingredient);

                                return (
                                    <button
                                        key={index}
                                        className={`deselected-ingredient-item ${inCupboard ? 'in-cupboard' : ''} ${isUnmatched ? 'ingredient-unmatched' : ''}`}
                                        onClick={() => handleIngredientToggle(ingredient.name)}
                                        title={`Click to add ${ingredient.name} back to shopping list`}
                                    >
                                        {ingredient.name} ({formatIngredientAmount(dispAmt, dispUnit)})
                                        {inCupboard && <span className="cupboard-icon">🏠</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            

            <div className="cost-summary-modern">
                {/* Cupboard summary */}
                {cupboardIngredients.length > 0 && (
                    <div className="cupboard-summary-modern">
                        <div className="cupboard-stats-modern">
                            <div className="stat-item-modern">
                                <div className="stat-icon">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <span className="stat-label-modern">In Cupboard:</span>
                                    <span className="stat-value-modern">
                                        {ingredients.filter(ingredient => 
                                            cupboardIngredients.some(cupboardItem => 
                                                cupboardItem.ingredientName.toLowerCase() === ingredient.name.toLowerCase()
                                            )
                                        ).length} ingredients
                                    </span>
                                </div>
                            </div>
                            <div className="stat-item-modern">
                                <div className="stat-icon">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <span className="stat-label-modern">Need to Buy:</span>
                                    <span className="stat-value-modern">
                                        {ingredients.filter(ingredient => 
                                            !cupboardIngredients.some(cupboardItem => 
                                                cupboardItem.ingredientName.toLowerCase() === ingredient.name.toLowerCase()
                                            )
                                        ).length} ingredients
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="cost-breakdown-modern">
                    <div className="cost-item-modern">
                        <span className="cost-label-modern">Original Total:</span>
                        <span className="original-price-modern">R{originalTotal.toFixed(2)}</span>
                    </div>
                    <div className="cost-item-modern savings">
                        <span className="cost-label-modern">Total Savings:</span>
                        <span className="savings-amount-modern">
                            R{totalSavings.toFixed(2)} 
                            {originalTotal > 0 && ` (${((totalSavings / originalTotal) * 100).toFixed(1)}%)`}
                        </span>
                    </div>
                    <div className="cost-item-modern final-total">
                        <span className="cost-label-modern">Final Total:</span>
                        <span className="final-price-modern">R{totalCost.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            <button 
                onClick={handleOrderIngredients}
                className="order-button-modern"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                </svg>
                <span>Add to Shopping List</span>
            </button>
            
            {/* Search functionality removed */}

            {/* Deselect Popup Modal */}
            {showDeselectPopup && deselectIngredient && (
                <div 
                    className="deselect-popup-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            cancelDeselect();
                        }
                    }}
                >
                    <div className="deselect-popup">
                        {/* Mobile Drag Handle */}
                        <div className="deselect-popup-drag-handle"></div>
                        <div className="deselect-popup-header">
                            <h3>Deselect Ingredient</h3>
                            <button 
                                onClick={cancelDeselect}
                                className="deselect-popup-close"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="deselect-popup-content">
                            <p>
                                You're about to deselect <strong>{deselectIngredient.name}</strong> ({deselectIngredient.amount} {deselectIngredient.unit}).
                            </p>
                            <p>What would you like to do?</p>
                        </div>
                        
                        <div className="deselect-popup-actions">
                            <button 
                                onClick={handleDeselectWithCupboard}
                                disabled={isAddingToCupboard}
                                className="deselect-popup-btn cupboard-btn"
                            >
                                {isAddingToCupboard ? (
                                    <>
                                        <span className="loading-spinner"></span>
                                        Adding to Cupboard...
                                    </>
                                ) : (
                                    <>
                                        🏠 Add to Cupboard & Deselect
                                    </>
                                )}
                            </button>
                            
                            <button 
                                onClick={handleDeselectOnly}
                                disabled={isAddingToCupboard}
                                className="deselect-popup-btn deselect-only-btn"
                            >
                                Just Deselect for This Recipe
                            </button>
                            
                            <button 
                                onClick={cancelDeselect}
                                disabled={isAddingToCupboard}
                                className="deselect-popup-btn cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
